/**
 * PermissionCatalogPage —— 只读权限目录（/governance/permissions）。
 *
 * 按 域 → 资源 → 动作 三级分组展示全部 ACTIVE 权限，供管理员查阅。
 * 数据走 governanceApi.getPermissionCatalog()（已按 sort_order 排好序、仅含 ACTIVE）。
 * 全部用 wxb-ui 组件、CSS 变量配色、无 emoji（图标用内联 SVG / WxbIcon）。
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  WxbPageShell,
  WxbPageHeader,
  WxbButton,
  WxbSearchInput,
  WxbSpinner,
  WxbEmpty,
  WxbBadge,
  WxbTag,
  WxbCollapse,
  WxbDataTable,
  WxbIcon,
} from '../../components/wxb-ui';
import { governanceApi, PermissionDomainGroup } from '../../services/governanceApi';
import { wxbToast } from '../../components/wxb-ui/Toast/Toast';
import { useAuth } from '../../contexts/AuthContext';
import type { EffectivePermissionGrant } from '../../services/authApi';

const PermissionCatalogPage: React.FC = () => {
  const { currentSite, permissionGrants } = useAuth();
  const [catalog, setCatalog] = useState<PermissionDomainGroup[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>('');

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await governanceApi.getPermissionCatalog();
      setCatalog(data);
    } catch (err) {
      setError(true);
      wxbToast.error('加载权限目录失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const totalCount = useMemo(
    () =>
      catalog.reduce(
        (sum, domain) =>
          sum + domain.resources.reduce((rSum, resource) => rSum + resource.actions.length, 0),
        0,
      ),
    [catalog],
  );

  // 关键词过滤：匹配权限中文名 / 权限码 / 资源标签 / 域标签。
  const filtered = useMemo<PermissionDomainGroup[]>(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return catalog;
    return catalog
      .map((domain) => {
        const domainHit = domain.label.toLowerCase().includes(kw) || domain.domain.toLowerCase().includes(kw);
        const resources = domain.resources
          .map((resource) => {
            const resourceHit =
              resource.label.toLowerCase().includes(kw) || resource.resourceCode.toLowerCase().includes(kw);
            const actions = resource.actions.filter(
              (action) =>
                action.permissionName.toLowerCase().includes(kw) ||
                action.permissionCode.toLowerCase().includes(kw) ||
                action.actionCode.toLowerCase().includes(kw),
            );
            if (domainHit || resourceHit) return resource;
            if (actions.length > 0) return { ...resource, actions };
            return null;
          })
          .filter((r): r is NonNullable<typeof r> => r !== null);
        if (domainHit) return domain;
        if (resources.length > 0) return { ...domain, resources };
        return null;
      })
      .filter((d): d is NonNullable<typeof d> => d !== null);
  }, [catalog, keyword]);

  const collapseItems = useMemo(
    () =>
      filtered.map((domain) => {
        const domainCount = domain.resources.reduce((sum, r) => sum + r.actions.length, 0);
        return {
          key: domain.domain,
          label: (
            <span className="gv-catalog-domain-label">
              <span className="gv-catalog-domain-name">{domain.label}</span>
              <WxbBadge variant="code" label={domain.domain} status="info" />
              <span className="gv-catalog-domain-count">{domainCount} 项权限</span>
            </span>
          ),
          children: (
            <div className="gv-catalog-resources">
              {domain.resources.map((resource) => (
                <div className="gv-catalog-resource" key={`${domain.domain}-${resource.resourceCode}`}>
                  <div className="gv-catalog-resource-head">
                    <span className="gv-catalog-resource-name">{resource.label}</span>
                    <WxbBadge variant="outline" code="资源" label={resource.resourceCode} status="neutral" />
                  </div>
                  <div className="gv-catalog-actions">
                    {resource.actions.map((action) => (
                      <div className="gv-catalog-action" key={action.permissionCode}>
                        <div className="gv-catalog-action-main">
                          <span className="gv-catalog-action-name">{action.permissionName}</span>
                          <WxbTag color="blue">{action.actionCode}</WxbTag>
                        </div>
                        <code className="gv-catalog-action-code">{action.permissionCode}</code>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ),
        };
      }),
    [filtered],
  );

  const provenanceColumns = useMemo(
    () => [
      {
        title: '权限码',
        dataIndex: 'permissionCode',
        key: 'permissionCode',
        render: (value: string) => (
          <WxbBadge variant="code" label={value} status="info" />
        ),
      },
      {
        title: '来源角色',
        key: 'role',
        render: (_: unknown, grant: EffectivePermissionGrant) => (
          <span className="gv-permission-role">
            <span>{grant.roleCode}</span>
            <WxbTag color="neutral">v{grant.roleVersion}</WxbTag>
          </span>
        ),
      },
      {
        title: 'Grant 范围',
        key: 'scope',
        render: (_: unknown, grant: EffectivePermissionGrant) => (
          <WxbTag color={grant.scopeType === 'SITE' ? 'blue' : 'cyan'}>
            {grant.scopeType}
            {grant.scopeOrgUnitId == null ? '' : ` · 组织 #${grant.scopeOrgUnitId}`}
          </WxbTag>
        ),
      },
      {
        title: '有效期',
        key: 'effectiveInterval',
        render: (_: unknown, grant: EffectivePermissionGrant) => (
          <span className="gv-permission-interval">
            {grant.effectiveFrom} — {grant.effectiveTo ?? '长期有效'}
          </span>
        ),
      },
      {
        title: 'Grant ID',
        dataIndex: 'assignmentId',
        key: 'assignmentId',
        render: (value: number) => (
          <code className="gv-catalog-action-code">#{value}</code>
        ),
      },
    ],
    [],
  );

  const renderBody = () => {
    if (loading) {
      return (
        <div className="gv-page-loading">
          <WxbSpinner tip="加载权限目录中…" />
        </div>
      );
    }
    if (error) {
      return (
        <WxbEmpty
          description="加载权限目录失败"
          action={
            <WxbButton variant="secondary" onClick={loadCatalog}>
              重试
            </WxbButton>
          }
        />
      );
    }
    if (filtered.length === 0) {
      return <WxbEmpty description={keyword ? '没有匹配的权限' : '暂无权限数据'} />;
    }
    return (
      <WxbCollapse
        key={keyword.trim()}
        items={collapseItems}
        defaultActiveKeys={filtered.map((d) => d.domain)}
      />
    );
  };

  return (
    <WxbPageShell>
      <WxbPageHeader
        eyebrow="权限治理"
        title="权限目录"
        description="系统全部可授予权限的只读视图，按业务域 → 资源 → 动作分组展示，供管理员查阅与配置角色时参照。"
        meta={!loading && !error ? <WxbBadge variant="bar" status="info" label={`共 ${totalCount} 项权限`} /> : undefined}
        actions={
          <>
            <div className="gv-catalog-search">
              <WxbSearchInput
                placeholder="搜索权限名 / 权限码 / 资源"
                value={keyword}
                onChange={setKeyword}
                allowClear
              />
            </div>
            <WxbButton variant="secondary" onClick={loadCatalog} disabled={loading}>
              <span className="gv-refresh-label">
                <WxbIcon name="refresh" size={15} />
                刷新
              </span>
            </WxbButton>
          </>
        }
      />
      <div className="gv-page-content">
        <section className="gv-permission-provenance" aria-labelledby="effective-permissions-title">
          <div className="gv-permission-provenance-head">
            <div>
              <h2 id="effective-permissions-title">我的有效权限来源</h2>
              <p>
                {currentSite
                  ? `${currentSite.siteName}（${currentSite.siteCode}）中的实时授权快照`
                  : '当前没有可用 Site'}
              </p>
            </div>
            <WxbBadge
              variant="bar"
              status="success"
              label={`${permissionGrants.length} 条有效 Grant`}
            />
          </div>
          <WxbDataTable<EffectivePermissionGrant>
            rowKey={(grant) => `${grant.permissionCode}-${grant.assignmentId}`}
            columns={provenanceColumns}
            dataSource={permissionGrants}
            pagination={{ pageSize: 10, hideOnSinglePage: true }}
            density="compact"
            emptyState={{ description: '当前 Site 没有有效权限 Grant' }}
          />
        </section>
        {renderBody()}
      </div>
      <PermissionCatalogStyles />
    </WxbPageShell>
  );
};

/** 页内局部样式：仅用 CSS 变量，不写死 hex。 */
const PermissionCatalogStyles: React.FC = () => (
  <style>{`
    .gv-page-loading { min-height: 40vh; display: flex; align-items: center; justify-content: center; }
    .gv-page-content { margin-top: var(--wx-space-16); }
    .gv-permission-provenance { margin-bottom: var(--wx-space-24, 24px); }
    .gv-permission-provenance-head { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--wx-space-16, 16px); margin-bottom: var(--wx-space-12, 12px); }
    .gv-permission-provenance-head h2 { margin: 0; font-size: var(--wx-fs-18, 18px); color: var(--wx-fg-1); }
    .gv-permission-provenance-head p { margin: var(--wx-space-4) 0 0; color: var(--wx-fg-3); }
    .gv-permission-role { display: inline-flex; align-items: center; gap: var(--wx-space-8, 8px); }
    .gv-permission-interval { color: var(--wx-fg-2); font-size: var(--wx-fs-12, 12px); }
    .gv-catalog-search { width: 240px; }
    .gv-refresh-label { display: inline-flex; align-items: center; gap: var(--wx-space-6); }
    .gv-catalog-domain-label { display: inline-flex; align-items: center; gap: var(--wx-space-12, 12px); }
    .gv-catalog-domain-name { font-weight: 600; color: var(--wx-fg-1); }
    .gv-catalog-domain-count { font-size: var(--wx-fs-12, 12px); color: var(--wx-fg-3); }
    .gv-catalog-resources { display: flex; flex-direction: column; gap: var(--wx-space-16, 16px); padding: var(--wx-space-8, 8px) 0; }
    .gv-catalog-resource { border: 1px solid var(--wx-border); border-radius: var(--wx-radius-md, 8px); padding: var(--wx-space-12, 12px) var(--wx-space-16, 16px); background: var(--wx-surface-1); }
    .gv-catalog-resource-head { display: flex; align-items: center; gap: var(--wx-space-8, 8px); margin-bottom: var(--wx-space-12, 12px); }
    .gv-catalog-resource-name { font-weight: 600; color: var(--wx-fg-2); }
    .gv-catalog-actions { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: var(--wx-space-8, 8px); }
    .gv-catalog-action { display: flex; flex-direction: column; gap: var(--wx-space-4); padding: var(--wx-space-8, 8px) var(--wx-space-12, 12px); border-radius: var(--wx-radius-sm, 6px); background: var(--wx-surface-2); }
    .gv-catalog-action-main { display: flex; align-items: center; gap: var(--wx-space-8, 8px); justify-content: space-between; }
    .gv-catalog-action-name { font-size: var(--wx-fs-14, 14px); color: var(--wx-fg-1); }
    .gv-catalog-action-code { font-size: var(--wx-fs-12, 12px); color: var(--wx-fg-3); font-family: var(--wx-font-mono, ui-monospace, monospace); }
  `}</style>
);

export default PermissionCatalogPage;
